import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import t from 'react-translate';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { Recruitment, RecruitmentApplication } from 'redux/schemas/models/mentoring-program-recruitments';


type MentoringProgramRecruitmentParams = {
  programId: number;
  recruitmentId?: number;
  recruitment?: Recruitment;
  role?: 'mentor' | 'mentee';
  mentorAlias?: string;
  mentorsAlias?: string;
};

// This is "Enabling Mentor/Mentee Recruitment"
export const createMentoringProgramRecruitment = createAsyncThunk<
Recruitment,
MentoringProgramRecruitmentParams
>(
  'CREATE_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId, role = 'mentor', recruitment }) => {
    const response = await axios.post(
      `/mentorship_programs/${programId}/mentorship_program_recruitments`,
      {
        mentorship_program_recruitment: {
          ...recruitment,
          role,
        }
      },
    );

    return response.data.result;
  },
);

export const updateMentoringProgramRecruitment = createAsyncThunk<
Recruitment,
MentoringProgramRecruitmentParams
>(
  'UPDATE_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId, recruitment }) => {
    const response = await axios.put(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitment?.id}`,
      {
        mentorship_program_recruitment: {
          ...recruitment,
        },
      },
    );

    return response.data.result;
  },
);

// This is "Disabling Mentor Recruitment"
export const deleteMentoringProgramRecruitment = createAsyncThunk<
boolean,
MentoringProgramRecruitmentParams
>(
  'DELETE_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId, recruitment }) => {
    const response = await axios.delete(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitment?.id}`,
    );

    return response.data.result;
  },
);

export const getMentoringProgramRecruitment = createAsyncThunk<
Recruitment,
MentoringProgramRecruitmentParams
>(
  'GET_MENTORING_PROGRAM_RECRUITMENT',
  async ({ programId, recruitmentId }) => {
    const response = await axios.get(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}`,
    );

    return response.data.result;
  },
);

/**
 * Recruitment Application APIs
 */

// This is "Express Interest"
export const createMentoringProgramRecruitmentApplication = createAsyncThunk<
RecruitmentApplication,
MentoringProgramRecruitmentParams
>(
  'CREATE_MENTORING_PROGRAM_RECRUITMENT_APPLICATION',
  async ({ programId, recruitmentId }) => {
    const response = await axios.post(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications`,
    );

    return response.data.result;
  },
);

export const getCurrentMentoringProgramRecruitmentApplication = createAsyncThunk<
RecruitmentApplication | null,
MentoringProgramRecruitmentParams
>(
  'GET_CURRENT_MENTORING_PROGRAM_RECRUITMENT_APPLICATION',
  async ({ programId, recruitmentId }) => {
    try {
      const response = await axios.get(
        `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications/current`,
      );

      return response.data.result;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      throw error;
    }
  },
);

// This is "Withdraw Interest"
export const deleteMentoringProgramRecruitmentApplication = createAsyncThunk<
boolean,
MentoringProgramRecruitmentParams
>(
  'DELETE_MENTORING_PROGRAM_RECRUITMENT_APPLICATION',
  async ({ programId, recruitmentId }, thunkAPI) => {
    const response = await axios.delete(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications/current`,
    );

    thunkAPI.dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.FORM.SUCCESS_BANG(),
      message: t.MENTORING_PROGRAMS.FLYER.INTEREST_HAS_BEEN_WITHDRAWN(),
    }));

    return response.data.result;
  },
);

const sortByToParamPrefix = {
  name: 'name_',
  dateSubmitted: 'date_submitted_',
};

type GetMentoringProgramRecruitmentApplicationsParams = MentoringProgramRecruitmentParams & {
  page?: number;
  pageSize?: number;
  textSearch?: string;
  sortBy?: 'name' | 'dateSubmitted';
  sortDesc?: boolean;
};

export const getMentoringProgramRecruitmentApplications = createAsyncThunk<
RecruitmentApplication[],
GetMentoringProgramRecruitmentApplicationsParams
>(
  'GET_MENTORING_PROGRAM_RECRUITMENT_APPLICATIONS',
  async ({ programId, recruitmentId, page, pageSize, textSearch, sortBy, sortDesc }) => {
    let sortingParam;
    if (sortBy) {
      const suffix = sortDesc ? 'desc' : 'asc';
      sortingParam = `${sortByToParamPrefix[sortBy]}${suffix}`;
    }

    const params = {
      page,
      page_size: pageSize,
      text_search: textSearch || undefined,
    };

    if (sortingParam) {
      params[sortingParam] = true;
    }

    const response = await axios.get(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications`,
      {
        params,
      },
    );

    return response.data.result;
  },
);

type BulkEnrollMentoringProgramRecruitmentApplicationsResponse = {
  existing: { userId: number; email: string }[];
  newlyEnrolled: { userId: number; email: string }[];
};

export const bulkEnrollMentoringProgramRecruitmentApplications = createAsyncThunk<
BulkEnrollMentoringProgramRecruitmentApplicationsResponse,
MentoringProgramRecruitmentParams & { applicationIds: number[] }
>(
  'BULK_ENROLL_MENTORING_PROGRAM_RECRUITMENT_APPLICATIONS',
  async ({ programId, recruitmentId, applicationIds, mentorAlias, mentorsAlias }, thunkAPI) => {
    const response = await axios.post(
      `/mentorship_programs/${programId}/mentorship_program_recruitments/${recruitmentId}/mentorship_program_recruitment_applications/bulk_enroll`,
      {
        mentorship_program_recruitment_application_ids: applicationIds,
      },
    );

    const { newlyEnrolled } = response.data.result;
    thunkAPI.dispatch(addAlertMessage({
      type: AlertMessageType.SUCCESS,
      header: t.FORM.SUCCESS_BANG(),
      message: t.MENTORING_PROGRAMS.RECRUITMENT.MENTORS_ADDED_SUCCESS({
        count: newlyEnrolled.length,
        mentorAlias,
        mentorsAlias,
      }),
    }));

    return response.data.result;
  },
);
